import * as React from "react"
import styled from "styled-components/macro"
import DOMPurify from "dompurify"
import { useCallback } from "react"
import { useMapStore } from "@common/features/GoogleMaps/mapStore"
import SvgIcon from "@common/ui/SvgIcon"
import IconLabel from "@common/ui/IconLabel"
import TextButton from "@common/ui/TextButton"

type TravelMode = {
  mode: string
  iconUrl: string
  label: string
}

const travelModes: TravelMode[] = [
  { mode: "WALKING", iconUrl: "/assets/icons/walking.svg", label: "Walking" },
  { mode: "DRIVING", iconUrl: "/assets/icons/driving.svg", label: "Driving" },
  { mode: "TRANSIT", iconUrl: "/assets/icons/tube.svg", label: "Transit" },
  { mode: "BICYCLING", iconUrl: "/assets/icons/cycling.svg", label: "Cycling" },
]

export interface IDirectionsPanelProps {
  panelRef?: React.RefObject<HTMLDivElement>
  routeSteps: google.maps.DirectionsStep[]
  onClose?: () => void
}

export default function DirectionsPanel(props: IDirectionsPanelProps) {
  const { routeSteps, onClose } = props

  const { setTravelMode, travelMode, activePOI } = useMapStore((state) => ({
    travelMode: state.travelMode,
    setTravelMode: state.setTravelMode,
    activePOI: state.activePOI,
  }))
  console.log(routeSteps[0]?.travel_mode, travelMode)

  const handleRouteCalculation = useCallback(
    (travelMode: string) => {
      setTravelMode(travelMode as any)
    },
    [setTravelMode],
  )

  return (
    <Wrapper>
      <div className="header">
        <TextButton
          style={{ borderRadius: "10rem", gap: "1em", margin: 0, padding: 0 }}
          color="var(--black)"
          variant="text"
          iconRound
          icon="/assets/icons/cross.svg"
          onClick={onClose}
        >
          Close
        </TextButton>
      </div>

      <div className="content" style={{ paddingBottom: "2em" }}>
        <div className="travel-modes-container">
          <div className="directions-text">{activePOI?.name}</div>
          <div className="travel-modes">
            {travelModes.map(({ mode, iconUrl, label }) => (
              <div
                key={mode}
                className={`mode ${mode.toLowerCase()}`}
                onClick={() => handleRouteCalculation(mode)}
              >
                <span
                  className={`icon-wrapper ${
                    travelMode === mode ? "active" : ""
                  }`}
                >
                  <SvgIcon
                    url={iconUrl}
                    color={
                      travelMode === mode ? "#fff" : "rgba(255, 255, 255, 0.3)"
                    }
                  />
                </span>
                <span>
                  {mode === "TRANSIT" &&
                  activePOI?.distances?.TRANSIT?.duration ===
                    activePOI?.distances?.WALKING?.duration &&
                  activePOI?.distances?.TRANSIT?.distance ===
                    activePOI?.distances?.WALKING?.distance
                    ? "N/A"
                    : activePOI?.distances?.[mode]?.duration}
                </span>
              </div>
            ))}
          </div>

          <div className="distance-icon">
            {activePOI?.distances && (
              <IconLabel
                icon={`/assets/icons/distance.svg`}
                iconColor="var(--black)"
                label={activePOI?.distances?.[travelMode]?.distance}
                variant="ghost"
              />
            )}
          </div>
        </div>
      </div>
      {routeSteps[0]?.travel_mode === "WALKING" && travelMode === "TRANSIT" ? (
        <div className="noInstructions">
          No tube service available to this destination. Please consider walking
          instead!
        </div>
      ) : (
        <div className="content directions-steps">
          <div className="custom-panel">
            <ul className="steps">
              {routeSteps.map((step, index) => {
                const sanitizedInstructions = DOMPurify.sanitize(
                  step.instructions,
                )
                const distance = step?.distance
                const duration = step?.duration

                return (
                  <li key={index}>
                    <div className="rombus"></div>
                    <div
                      className="instructions"
                      dangerouslySetInnerHTML={{
                        __html: sanitizedInstructions,
                      }}
                    ></div>
                    <div className="time">
                      {duration?.text}
                      <span style={{ margin: "1em" }} /> {distance?.text}
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: ThirdFont-Regular;

  .header {
    display: flex;
    padding: 2em 3em;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    text-transform: uppercase;
  }

  .noInstructions {
    color: var(--primary);
    margin: 2rem auto;
    padding: 2rem;
    text-align: center;
  }

  .content {
    display: flex;
    flex-direction: column;
    padding: 3em;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    color: var(--black);

    &.directions-steps {
      flex: 1;
      padding: 1em 3em;
      overflow-y: auto;
      height: 100%;

      &::-webkit-scrollbar {
        width: 0.3em;
      }

      &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.1);
        border-radius: 1rem;
      }
    }

    .travel-modes-container {
      text-transform: uppercase;

      .directions-text {
        font-size: 1.5em;
        font-weight: bold;
        margin-bottom: 2em;
      }

      .travel-modes {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .mode {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1em;
          cursor: pointer;

          .icon-wrapper {
            height: 4em;
            width: 4em;
            padding: 1em;
            transition: background 0.3s;
            border-radius: 10rem;
            background-color: var(--black);
          }

          span {
            font-size: 1em;
          }

          img {
            width: 3em;
            height: 3em;
            padding: 0.8em;
          }
        }
      }

      .distance-icon {
        margin-top: 2em;
      }
    }

    .custom-panel {
      color: #252525d6;

      .header {
        font-size: 1.3em;
        font-weight: bold;
        padding: 1rem 0;
      }

      .steps {
        position: relative;
        list-style: none;
        display: flex;
        flex-direction: column;
        margin: 0;
        padding-left: 1em;

        .line {
          width: 0px;
          height: 100%;
          position: absolute;
        }
      }

      li {
        position: relative;
        padding: 1em 0em;

        &:last-child {
          border-left: 1.5px dashed transparent;
        }

        .rombus {
          position: absolute;
          width: 0.6rem;
          height: 0.6rem;
          background-color: #252525d6;
          top: 1.6em;
          transform: rotate(45deg);
          margin-left: -1rem;
        }

        .time {
          opacity: 0.6;
          font-weight: 600;
          font-size: 0.7em;
          position: relative;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          line-height: 2;
          margin-left: 1em;
        }

        .instructions {
          line-height: 1.5;
          font-size: 1.3em;
          margin-left: 0.5em;
        }
      }
    }
  }
`
